import { useClipboard } from '@vueuse/core'

export default function useDOM() {
  const { copy } = useClipboard({ legacy: true })
  const { create } = useNotification()
  const { t } = useI18n()

  const copyToClipboard = async (
    text: string,
    successNotificationText = t('global.copyToClipboard.success'),
    errorNotificationText = t('global.copyToClipboard.error')
  ) => {
    try {
      await copy(text)
      create('success', {
        text: successNotificationText,
        timeout: 2000
      })
    } catch {
      create('error', {
        text: `${errorNotificationText} ${text}`,
        persist: true
      })
    }
  }

  const openInNewTab = (url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank').focus()
      return
    }
    window.open(`http://${url}`, '_blank').focus()
  }

  const isImgSquare = (img: HTMLImageElement) => {
    const width = img.naturalWidth
    const height = img.naturalHeight
    const isSquare = width / height < 2
    return isSquare
  }

  const downloadQrCode = async (element: HTMLElement, filename: string) => {
    await nextTick()

    const findCanvas = (el: HTMLElement): HTMLCanvasElement | null => {
      if (el instanceof HTMLCanvasElement) {
        return el
      }

      const canvas = el.querySelector('canvas')
      if (canvas) {
        return canvas as HTMLCanvasElement
      }

      const siblingCanvas = el.nextElementSibling instanceof HTMLCanvasElement ? el.nextElementSibling : null
      if (siblingCanvas) {
        return siblingCanvas
      }

      return null
    }

    const canvas = findCanvas(element)

    if (!canvas || !canvas.toDataURL) {
      throw new Error('Canvas element not found or invalid')
    }

    const finalFilename = filename.toLowerCase().endsWith('.png') ? filename : `${filename}.png`

    const link = document.createElement('a')
    link.download = finalFilename
    link.href = canvas.toDataURL('image/png')
    link.click()
  }

  const delay = (milliseconds: number) => {
    return new Promise((resolve) => {
      setTimeout(resolve, milliseconds)
    })
  }

  const scrollToBlockBottom = (ref: HTMLElement) => {
    nextTick(() => {
      ref.scrollIntoView({ behavior: 'smooth' })
    })
  }

  return { copyToClipboard, openInNewTab, isImgSquare, downloadQrCode, delay, scrollToBlockBottom }
}
